import { useApolloClient } from '@apollo/client';
import { Button, TextField } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Auth from '../../helpers/auth';
import { useVerifyOtp } from '../../hooks';
import useMFAConfig from '../../hooks/gql/useMFAConfig';
import useSendOtp from '../../hooks/gql/useSendOtp';

export const TwoFactorForm: React.FC = () => {
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [verifyError, setVerifyError] = useState<string>('');
  const [otpMethod, setOtpMethod] = useState<'email' | 'sms' | 'totp'>('email');
  const client = useApolloClient();
  const location = useLocation<{ from?: { pathname?: string } }>();
  const history = useHistory();
  const {data} = useMFAConfig();
  const [sendOtp] = useSendOtp();
  const [verifyOtp] = useVerifyOtp();
  const mfaConfig = data?.mfaConfig;

  useEffect(() => {
    if (mfaConfig) {
      setOtpMethod(mfaConfig.totpEnabled ? 'totp' : 'email');
    }
  }, [mfaConfig]);

  const handleSendCode = async (method: 'email' | 'sms') => {
    await sendOtp({ variables: { method } });
    setOtpMethod(method);
  };

  const handleUseAuthenticatorApp = () => {
    setOtpMethod('totp');
  };

  async function handleAuthenticationWithToken(token: string) {
    Auth.setToken(token);
    await client.cache.reset();
    await client.resetStore();

    const state = location.state;
    const path = state?.from?.pathname ?? '/';
    if (path && path !== '/login') {
      history.push(path);
    } else {
      history.push('/');
    }
  }

  const handle2FaVerify = async () => {
    try {
      await verifyOtp({
        variables: { code: twoFactorCode, method: otpMethod },
        update: async (_cache, result) => {
          const { data } = result;
          const sessionToken = data?.verifyOtp?.sessionToken || data?.verifyOtp?.sessionToken;
          if (sessionToken) {
            await handleAuthenticationWithToken(sessionToken);
          }
        }
      });
    } catch (error) {
      setVerifyError('Failed to verify OTP. Please try again.');
    }
  };

  return (
    <>
      {mfaConfig?.enabled && <Button onClick={() => handleSendCode('email')}>Send code to email</Button>}
      {mfaConfig?.smsOtpEnabled && <Button onClick={() => handleSendCode('sms')}>Send code to SMS</Button>}
      {(mfaConfig?.totpEnabled && otpMethod === 'totp') && <Button onClick={handleUseAuthenticatorApp}>Use authenticator app</Button>}
      <TextField
        fullWidth
        id="twoFactorRedirect"
        name="twoFactorRedirect"
        label={otpMethod === 'totp' ? 'Enter code from authenticator app' : 'Enter code'}
        onChange={ev => setTwoFactorCode(ev.target.value)}
        value={twoFactorCode}
      />
      <Button onClick={() => handle2FaVerify()}>Verify</Button>
      {verifyError && <p style={{ color: 'red' }}>{verifyError}</p>}
    </>
  );
};
